/**
 * List — dense sortable table over the same URL-driven filters as the Board.
 *
 * Keyboard: `j`/`k` (or arrows) move the cursor, `Enter` opens the ticket,
 * `g g` / `G` jump to first/last, `/` focuses the search box.
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import FilterBar from '../components/FilterBar.jsx'
import { Chip, Priority } from '../components/bits.jsx'
import { STATUS_LABELS, useFilters } from '../lib/filters.js'
import { useTickets } from '../lib/useTickets.js'
import { ago, userName } from '../lib/format.js'
import { useTaxonomy } from '../lib/taxonomy.jsx'

/** Sortable columns: label plus the API sort key. */
const COLUMNS = [
  { key: 'key', label: 'Key', width: 88 },
  { key: 'priority', label: 'Pri', width: 46 },
  { key: 'status', label: 'Status', width: 104 },
  { key: 'kind', label: 'Kind', width: 108 },
  { key: 'title', label: 'Title', grow: true },
  { key: null, label: 'City', width: 150 },
  { key: null, label: 'Component', width: 160 },
  { key: 'assignee_id', label: 'Assignee', width: 130 },
  { key: 'due_date', label: 'Due', width: 84 },
  { key: 'updated_at', label: 'Updated', width: 74 },
]

export default function List() {
  const { filters, toggle, setValue, clear } = useFilters()
  const { tickets, total, loading, error, blockers } = useTickets(filters)
  const { usersById } = useTaxonomy()
  const navigate = useNavigate()
  const [cursor, setCursor] = useState(0)
  const bodyRef = useRef(null)
  const pendingG = useRef(false)

  useEffect(() => {
    setCursor(0)
  }, [tickets.length])

  useEffect(() => {
    const onKey = (event) => {
      const tag = document.activeElement?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        if (event.key === 'Escape') document.activeElement.blur()
        return
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return

      if (event.key === '/') {
        event.preventDefault()
        document.querySelector('.filterbar input.input')?.focus()
        return
      }
      if (!tickets.length) return

      if (event.key === 'j' || event.key === 'ArrowDown') {
        event.preventDefault()
        setCursor((c) => Math.min(c + 1, tickets.length - 1))
      } else if (event.key === 'k' || event.key === 'ArrowUp') {
        event.preventDefault()
        setCursor((c) => Math.max(c - 1, 0))
      } else if (event.key === 'Enter') {
        event.preventDefault()
        const ticket = tickets[cursor]
        if (ticket) navigate(`/t/${ticket.key}`)
      } else if (event.key === 'G') {
        event.preventDefault()
        setCursor(tickets.length - 1)
      } else if (event.key === 'g') {
        if (pendingG.current) {
          pendingG.current = false
          setCursor(0)
        } else {
          pendingG.current = true
          setTimeout(() => {
            pendingG.current = false
          }, 600)
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [tickets, cursor, navigate])

  useEffect(() => {
    const row = bodyRef.current?.querySelector('tr.cursor')
    row?.scrollIntoView({ block: 'nearest' })
  }, [cursor, tickets])

  const currentSort = filters.sort
  const sortBy = (key) => {
    if (!key) return
    setValue('sort', currentSort === key ? `-${key}` : key)
  }
  const sortMark = (key) => {
    if (!key) return ''
    if (currentSort === key) return ' ↑'
    if (currentSort === `-${key}`) return ' ↓'
    return ''
  }

  return (
    <>
      <FilterBar
        filters={filters}
        toggle={toggle}
        setValue={setValue}
        clear={clear}
        count={total}
        showStatus
        right={
          <Link className="btn sm" to={{ pathname: '/', search: window.location.search }}>
            Open as board
          </Link>
        }
      />
      {error && <div className="notice error">{error.message}</div>}
      <div className="tablewrap" ref={bodyRef}>
        <table className="list">
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column.label}
                  style={column.grow ? undefined : { width: column.width }}
                  onClick={() => sortBy(column.key)}
                  title={column.key ? 'Sort' : undefined}
                >
                  {column.label}
                  {sortMark(column.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tickets.map((ticket, index) => (
              <tr
                key={ticket.id}
                className={`row${index === cursor ? ' cursor' : ''}`}
                onClick={() => {
                  setCursor(index)
                  navigate(`/t/${ticket.key}`)
                }}
              >
                <td className="key">
                  {ticket.key}
                  {ticket.closed_unverified && (
                    <span
                      className="badge-unverified"
                      style={{ marginLeft: 6 }}
                      title={ticket.unverified_reason || 'closed without evidence'}
                    >
                      no evidence
                    </span>
                  )}
                </td>
                <td>
                  <Priority value={ticket.priority} />
                </td>
                <td>{STATUS_LABELS[ticket.status]}</td>
                <td>{ticket.kind}</td>
                <td className="title">
                  {ticket.title}
                  {blockers.get(ticket.id)?.length > 0 && (
                    <>
                      {' '}
                      <Chip kind="blocked" title={`blocked by ${blockers.get(ticket.id).join(', ')}`}>
                        blocked by {blockers.get(ticket.id).length}
                      </Chip>
                    </>
                  )}
                </td>
                <td>{ticket.cities.map((c) => c.slug).join(', ')}</td>
                <td>{ticket.components.map((c) => c.slug).join(', ')}</td>
                <td>{ticket.assignee_id == null ? '—' : userName(usersById, ticket.assignee_id)}</td>
                <td>{ticket.due_date || ''}</td>
                <td>{ago(ticket.updated_at)}</td>
              </tr>
            ))}
            {!loading && tickets.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="notice">
                  No tickets match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="filterbar" style={{ borderTop: '1px solid var(--border)', borderBottom: 0 }}>
        <span className="kbdhint">
          <kbd>j</kbd> <kbd>k</kbd> move · <kbd>enter</kbd> open · <kbd>g g</kbd> / <kbd>G</kbd>{' '}
          first/last · <kbd>/</kbd> search
        </span>
      </div>
    </>
  )
}
